import { stringExtensions } from "lib-common";
import {
  Button, CreateButton, Drawer, getDefaultSortOrder, Icons, List, Table,
  Tag, TextField, Typography, useTable
} from "@pankod/refine-antd";
import { CanAccess, CrudFilters, HttpError, IResourceComponentsProps, useNavigation, useOne } from "@pankod/refine-core";
import { GroupsFilter } from "components/groups/groupsFilter";
import { ShowUserCard } from "components/profile/showUserCard";
import { displayRelativeDate } from "components/utils/displayRelativeDate";
import { IGroupsMetadata, IMemberGroup } from "interfaces";
import { useState } from "react";
import { DATAPROVIDER_CREATE, DATAPROVIDER_LOOKUP, DATAPROVIDER_READ, RESOURCE_PATH, STALE_DURATION } from "scripts/site";

export const GroupList: React.FC<IResourceComponentsProps> = () => {
  const { show } = useNavigation();
  const { data: metadata } = useOne<IGroupsMetadata>({
    dataProviderName: DATAPROVIDER_LOOKUP,
    resource: RESOURCE_PATH.METADATA,
    id: RESOURCE_PATH.GROUPS,
    queryOptions: {
      enabled: true,
      staleTime: STALE_DURATION
    }
  });

  const { tableProps, sorter, tableQueryResult, searchFormProps } = useTable<IMemberGroup, HttpError, { q: string, state: string, groupType: string }>({
    dataProviderName: DATAPROVIDER_READ,
    initialSorter: [
      {
        field: "updatedAt",
        order: "desc"
      },
    ],
    onSearch: (params) => {
      const filters: CrudFilters = [];
      const { q, state, groupType } = params;
      filters.push({
        field: "q",
        operator: "contains",
        value: q
      });
      filters.push({
        field: "groupType",
        operator: "eq",
        value: groupType
      });
      filters.push({
        field: "stateManager.state",
        operator: "eq",
        value: state
      });
      return filters;
    }
  });

  function rowClick(record: IMemberGroup) {
    return {
      onClick: () => {
        show(RESOURCE_PATH.GROUPS, record.id);
      },
    };
  }

  const totalGroups = tableQueryResult?.data?.total ?? 0;
  const [filterOpen, switchFilterForm] = useState(false);
  const metaConfig = metadata?.data.config;

  return (
    <List
      headerProps={{
        title: "Helium Groups",
        tags: <Tag color={"default"}>
          {tableQueryResult.isLoading ? "Loading" : totalGroups} Group{totalGroups !== 1 && "s"}
        </Tag>
      }}
      headerButtons={() => (
        <>
          <CanAccess key={RESOURCE_PATH.GROUPS} resource={RESOURCE_PATH.GROUPS.toLowerCase()} action="create" params={{ dataProviderName: DATAPROVIDER_CREATE, id: "1" }}>
            <CreateButton></CreateButton>
          </CanAccess>
          <Button key="filter" icon={<Icons.SearchOutlined />} onClick={() => switchFilterForm(true)}>{metaConfig?.search?.textMessages?.buttonText || "Search & Filter"}</Button>
        </>
      )}
    >
      <Table {...tableProps}
        rowKey="id"
        onRow={rowClick}
        className="mouseHand"
        pagination={{
          ...tableProps.pagination,
          position: ["bottomRight"],
          showTotal: (total => <Typography.Title level={4} style={{ marginRight: 10 }} >Total {total}</Typography.Title>)
        }}
      >
        <Table.Column
          dataIndex="id"
          key="id"
          title="Group Identifier(Alias)"
          render={(value) =>
            <div>
              <TextField value={value} />
            </div>
          }
          defaultSortOrder={getDefaultSortOrder("id", sorter)}
          sorter
        />

        <Table.Column
          dataIndex="name"
          key="name"
          title="Description"
          render={(value) =>
            <div>
              <TextField value={value} />
            </div>
          }
          defaultSortOrder={getDefaultSortOrder("name", sorter)}
          sorter
        />

        <Table.Column
          dataIndex="groupType"
          title="Group Type"
          sorter
          render={(value) => <TextField value={stringExtensions.capitalize(value)} />}
        />

        <Table.Column
          dataIndex="admins"
          title="Group Admins"
          render={(value: string[]) =>
            value && value.map((r, i) => (
              <div key={i} style={{ marginBottom: 5 }}>
                <ShowUserCard id={r} />
              </div>
            ))
          }
        />

        <Table.Column
          dataIndex="createdBy"
          title="Created By"
          defaultSortOrder={getDefaultSortOrder("createdBy", sorter)}
          sorter
          render={(value) => <ShowUserCard id={value} />}
        />

        <Table.Column
          dataIndex="updatedAt"
          key="updatedAt"
          title="Last Updated"
          render={displayRelativeDate}
          defaultSortOrder={getDefaultSortOrder("updatedAt", sorter)}
          sorter
        />
      </Table>
      <Drawer
        title={metadata?.data?.config?.search?.textMessages?.drawerFormHeader || "Groups - Search & Filter"}
        placement="right"
        size="default"
        open={filterOpen}
        onClose={() => { switchFilterForm(false) }}
      >
        <GroupsFilter formProps={searchFormProps} />
      </Drawer>
    </List>
  );
};
